import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, tap } from 'rxjs';

import { ImpersonationActions } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';

// Reacts to a confirmed exit from the survey workflow — discard, save-and-exit,
// or save-and-exit-with-impersonation-change. Each effect owns exactly one
// outcome, split out of SurveyCreationSaveEffects so that class never has to
// know why a caller wanted to exit, only how to save.
@Injectable()
export class SurveyCreationWorkflowExitEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _router = inject(Router);

  public readonly discardSurveyChanges$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.discardSurveyChanges),
        tap(() => this._router.navigate([APP_ROUTES.DASHBOARD])),
      ),
    { dispatch: false },
  );

  public readonly disconnectImpersonationOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.saveSurveyAndDisconnectImpersonationSuccess,
      ),
      map(() => ImpersonationActions.disconnect()),
    ),
  );

  public readonly switchImpersonatedBuyerOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess,
      ),
      map(({ buyer }) => ImpersonationActions.switchBuyer(buyer)),
    ),
  );

  public readonly navigateToDashboardOnSurveyExitSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          SurveyCreationApiActions.saveSurveyAndExitSuccess,
          SurveyCreationApiActions.saveSurveyAndDisconnectImpersonationSuccess,
          SurveyCreationApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess,
        ),
        tap(() => this._router.navigate([APP_ROUTES.DASHBOARD])),
      ),
    { dispatch: false },
  );
}
